import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HardDrive, Loader2, Film, AlertTriangle, CheckCircle2, ArrowUpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

const fmtSize = (mb) => {
  const n = Number(mb || 0);
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} TB`;
  if (n >= 1024) return `${(n / 1024).toFixed(2)} GB`;
  return `${n.toFixed(1)} MB`;
};

export default function Usage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/usage/storage")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load usage"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-40"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;

  const pct = data?.percent ?? 0;
  const warn = data?.warn;
  const full = pct >= 100;

  return (
    <div className="p-6 lg:p-8 max-w-[860px] mx-auto">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight">Usage</h1>
        <p className="text-gray-400 mt-1.5 text-sm">How much of your plan's video storage you're using.</p>
      </div>

      {warn && (
        <motion.div data-testid="usage-warning" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className={"mb-6 flex items-start gap-3 rounded-xl border px-4 py-3 " + (full ? "border-red-500/40 bg-red-500/[0.08]" : "border-amber-500/40 bg-amber-500/[0.08]")}>
          <AlertTriangle className={"h-5 w-5 shrink-0 " + (full ? "text-red-400" : "text-amber-400")} />
          <div className="text-sm">
            <p className={"font-semibold " + (full ? "text-red-300" : "text-amber-300")}>
              {full ? "You've reached your storage limit" : `You're using ${pct}% of your storage`}
            </p>
            <p className="text-gray-400 mt-0.5">
              {full ? "New uploads may fail until you free up space or upgrade your plan." : "You're approaching your plan's storage limit. Consider upgrading to keep archiving your services."}
            </p>
          </div>
          <button data-testid="usage-upgrade" onClick={() => navigate("/billing")} className="ml-auto shrink-0 inline-flex items-center gap-1.5 gold-gradient text-black text-xs font-semibold px-3 py-2 rounded-lg hover:brightness-110">
            <ArrowUpCircle className="h-4 w-4" /> Upgrade
          </button>
        </motion.div>
      )}

      <div data-testid="usage-storage-card" className="bg-card border border-gray-800 rounded-2xl p-6">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <span className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center"><HardDrive className="h-5 w-5 text-amber-400" /></span>
            <div>
              <p className="text-white font-semibold">Video storage</p>
              <p className="text-xs text-gray-500 flex items-center gap-1"><Film className="h-3 w-3" /> {data?.videos ?? 0} video{(data?.videos ?? 0) === 1 ? "" : "s"} stored</p>
            </div>
          </div>
          {!warn && <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"><CheckCircle2 className="h-3.5 w-3.5" /> Healthy</span>}
        </div>

        <div className="flex items-baseline justify-between mb-2">
          <span className="font-display text-2xl font-bold text-white">{fmtSize(data?.used_mb)}</span>
          <span className="text-sm text-gray-400">of {data?.quota_gb} GB used <span className={warn ? "text-amber-300 font-semibold" : "text-gray-500"}>({pct}%)</span></span>
        </div>
        <div className="h-3 rounded-full bg-gray-800 overflow-hidden">
          <div className={"h-full rounded-full transition-all " + (full ? "bg-red-500" : warn ? "bg-gradient-to-r from-amber-500 to-red-500" : "gold-gradient")} style={{ width: `${Math.max(2, pct)}%` }} />
        </div>
        <p className="mt-4 text-[11px] text-gray-500">Storage is set by your plan. You'll see a warning here once you pass {data?.warn_percent ?? 80}% of your limit.</p>
      </div>
    </div>
  );
}
